import type { MenuItem } from '@/cms/fields/types/common'
import type { NavigationProps } from './types'
import { createHeaderClickEvent } from '@/domain/header/analytics'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'

import { space, tok, Typography } from '@sunwebgroup/ui-components'
import { focusWithinOutline, Icon } from '@sunwebgroup/ui-components/uds'
import { useState } from 'react'
import { styled } from 'styled-components'

import { HeaderLink } from './HeaderLink'
import { MenuTrigger } from './Menu'
import { Navbar } from './Navbar'
import { Sheet } from './Sheet'

const StyledLine = styled.hr`
  border-color: ${tok('semantics_dividers_color')};
  margin: ${space('primitives_space_12')} ${space('primitives_space_24')};
`

const BackButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${tok('semantics_content_secondary_color')};
  cursor: pointer;
  display: flex;
  padding: ${space('primitives_space_12')} 0;
  &:hover {
    color: ${tok('semantics_links_hover_color')};
  }

  ${Icon} {
    margin-right: ${space('primitives_space_4')};
  }
`

const SheetTrigger = styled(Sheet.Trigger)`
  ${focusWithinOutline()}//This is to enable the menu to have our focus outline when it is focused
`

export function MobileNavigation({ data, extraData }: NavigationProps) {
  const intl = useIntl()

  const [sheetIsOpen, setSheetIsOpen] = useState(false)

  // Use controlled state for the mobile menu's back button
  const [openMenu, setOpenMenu] = useState('')
  const [openSubMenu, setOpenSubMenu] = useState('')
  const { sendEvent } = useAnalytics()

  const handleMobileMenuItemClick = (result: string[], children: MenuItem[] | undefined) => {
    sendEvent(
      createHeaderClickEvent({
        result,
        hasChildren: children && children.length > 0,
      }),
    )
  }

  return (
    <Sheet.Root
      onOpenChange={(open) => {
        setSheetIsOpen(open)
      }}
      modal
    >
      <SheetTrigger>{sheetIsOpen ? <Icon glyph="DismissSmall" /> : <Icon glyph="List" />}</SheetTrigger>
      <Sheet.Overlay />
      <Sheet.Content>
        <Navbar value={openMenu} onValueChange={v => setOpenMenu(v)}>
          <Navbar.List>
            {data && <Typography $variant="body-md-bold">{data.text}</Typography>}
            {data?.children?.map((menuItem, menuItemKey) => (
              <Navbar.Item key={menuItemKey} value={menuItem.text}>
                <MenuTrigger
                  onClick={() => handleMobileMenuItemClick([data.text, menuItem.text], menuItem.children)}
                  onPointerMove={(e: React.PointerEvent) => e.preventDefault()}
                >
                  {menuItem.text}
                  <Icon glyph="ChevronRightExtraSmall" />
                </MenuTrigger>
                <Navbar.Content>
                  <BackButton onClick={() => setOpenMenu('')}>
                    <Icon glyph="ChevronLeftExtraSmall" />
                    <Typography>{intl.formatMessage({ id: 'back_link' })}</Typography>
                  </BackButton>
                  <Typography $variant="body-md-bold">{menuItem.text}</Typography>
                  <NavigationMenu.Sub value={openSubMenu} onValueChange={v => setOpenSubMenu(v)}>
                    <Navbar.List>
                      {menuItem.children?.map((subMenuItem, subMenuItemKey) => (
                        <NavigationMenu.Item key={subMenuItemKey} value={subMenuItem.text}>
                          {subMenuItem.children && subMenuItem.children.length > 0
                            ? (
                                <>
                                  <MenuTrigger
                                    onClick={() =>
                                      handleMobileMenuItemClick(
                                        [data.text, menuItem.text, subMenuItem.text],
                                        subMenuItem.children,
                                      )}
                                    onPointerMove={(e: React.PointerEvent) => e.preventDefault()}
                                  >
                                    {subMenuItem.text}
                                    <Icon glyph="ChevronRightExtraSmall" />
                                  </MenuTrigger>
                                  <Navbar.Content>
                                    <BackButton onClick={() => setOpenSubMenu('')}>
                                      <Icon glyph="ChevronLeft" />
                                      <Typography>{intl.formatMessage({ id: 'back_link' })}</Typography>
                                    </BackButton>
                                    <Typography $variant="body-md-bold">{subMenuItem.text}</Typography>
                                    <NavigationMenu.Sub>
                                      <Navbar.List>
                                        {subMenuItem.children.map((nestedSubMenuItem, nestedSubMenuItemKey) => (
                                          <NavigationMenu.Item key={nestedSubMenuItemKey}>
                                            <HeaderLink
                                              onClick={() =>
                                                handleMobileMenuItemClick(
                                                  [data.text, menuItem.text, subMenuItem.text, nestedSubMenuItem.text],
                                                  nestedSubMenuItem.children,
                                                )}
                                              href={nestedSubMenuItem.href}
                                            >
                                              {nestedSubMenuItem.text}
                                            </HeaderLink>
                                          </NavigationMenu.Item>
                                        ))}
                                      </Navbar.List>
                                    </NavigationMenu.Sub>
                                  </Navbar.Content>
                                </>
                              )
                            : (
                                <HeaderLink
                                  onClick={() =>
                                    handleMobileMenuItemClick(
                                      [data.text, menuItem.text, subMenuItem.text],
                                      subMenuItem.children,
                                    )}
                                  href={subMenuItem.href}
                                >
                                  {subMenuItem.text}
                                </HeaderLink>
                              )}
                        </NavigationMenu.Item>
                      ))}
                    </Navbar.List>

                    <NavigationMenu.Viewport />
                  </NavigationMenu.Sub>
                </Navbar.Content>
              </Navbar.Item>
            ))}

            <StyledLine />

            {extraData?.map((extraItem, extraItemKey) => (
              <Navbar.Item key={extraItemKey}>
                <HeaderLink
                  onClick={() => handleMobileMenuItemClick([extraItem.text], extraItem.children)}
                  href={extraItem.href}
                >
                  <Typography $variant="body-md-bold">{extraItem.text}</Typography>
                </HeaderLink>
              </Navbar.Item>
            ))}
          </Navbar.List>
        </Navbar>
      </Sheet.Content>
    </Sheet.Root>
  )
}
